import { useContext, useEffect, useState, useCallback } from "react";
import { nanoid } from "nanoid";
import {
  Dialog,
  DialogContent,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SalesContext from "context/SalesContext";
import ProductContext from "context/ProductContext";
import SalesPersonContext from "context/SalesPersonContext";
import styles from "./styles.module.css";
import KeyPadHorizontal from "components/utils/CustomKeypad/KeyPadHorizontal";
import {
  PRESET_NOTES,
  CUSTOM_PRODUCT_NOTES_KEY,
  ITEM_DETAILS_DIALOG_TABS,
  DEFAULT_PRODUCT_IMAGE,
  KEYBOARD_KEYS,
  TAX_PREFERENCES,
  EVENTS,
  SCANNER_RESTRICTED_INPUTS,
  STAFF_PERMISSION,
} from "const";
import { eventsEmitter } from "utils/events";
import TaxHelper from "helpers/TaxHelper";
import { formatName } from "components/utils/formatName";
import ShopContext from "context/ShopContext";
import ItemView from "../../utils/ItemView/ItemView";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import toast from "react-hot-toast";
import ProductPlaceholder from "images/product-placeholder.png";
import * as AppSettings from "helpers/AppSettingsHelper";
import { usePermission } from "hooks/usePermission";
import { getInitialRate } from "helpers/SalesHelper";

function getInitialSaleItemValues(saleItem) {
  const isExcluding = TaxHelper.getTaxPreference() === TAX_PREFERENCES.EXCLUDING_TAX;

  return {
    qty: saleItem?.qty || 0,
    discount: String(saleItem?.discountPercentage ?? 0),
    rate: getInitialRate(saleItem, isExcluding),
    salesperson_id: saleItem?.salesperson_id || null,
    note: saleItem?.note || "",
  };
}

function hasSaleItemChanges(initial, current) {
  return (
    current.qty !== initial.qty ||
    String(current.discount) !== initial.discount ||
    String(current.rate) !== initial.rate ||
    current.salesperson_id !== initial.salesperson_id ||
    current.note !== initial.note
  );
}
function updateSaleItem(
  saleItem,
  finalQty,
  selectedDiscount,
  selectedRate,
  note,
  selectedSalepersonId,
  rate,
  rateAndTax,
) {
  if (finalQty <= 0) return null;

  const initial = getInitialSaleItemValues(saleItem);

  const hasChanges = hasSaleItemChanges(initial, {
    qty: finalQty,
    discount: selectedDiscount,
    rate: selectedRate,
    salesperson_id: selectedSalepersonId,
    note,
  });

  if (!hasChanges) {
    return;
  }

  if (saleItem.localId) {
    return {
      ...saleItem,
      manualRate: Number(selectedRate),
      rate,
      rateAndTax,
      qty: finalQty,
      discountPercentage: Number(selectedDiscount) || 0,
      note,
      salesperson_id: selectedSalepersonId,
    };
  }

  return {
    localId: nanoid(),
    item: saleItem,
    name: saleItem.name,
    id: saleItem.id,
    qty: finalQty,
    rate,
    manualRate: Number(selectedRate),
    rateAndTax,
    note,
    discountPercentage: Number(selectedDiscount) || 0,
    salesperson_id: selectedSalepersonId,
    taxPercentage: saleItem.tax || 0,
    cessPercentage: saleItem.cess || 0,
    kotPrintedQty: 0,
    discount: 0,
    billLevelDiscount: 0,
    optionQty: finalQty,
    maxQty: saleItem?.maxQty ?? null,
  };
}

export default function ItemDetailsDialog({ open, saleItem, onClose }) {
  const item = saleItem?.item ?? saleItem;

  const { selectedShop } = useContext(ShopContext);
  const { items, addonMappings } = useContext(ProductContext);
  const { salesPersons } = useContext(SalesPersonContext);

  const { saleItemUpdateHandler, handleAddItemToSale, getSaleItemRate, activeSale } = useContext(SalesContext);

  const initialQty = saleItem?.qty || 1;
  const [activeSection, setActiveSection] = useState(ITEM_DETAILS_DIALOG_TABS.QUANTITY);
  const [selectedSalepersonId, setSelectedSalepersonId] = useState("");
  const [note, setNote] = useState("");
  const [productNotes, setProductNotes] = useState([]);

  const [qty, setQty] = useState(initialQty);
  const [selectedDiscount, setSelectedDiscount] = useState(String("")); // %
  const initialBaseRate = getSaleItemRate(saleItem, activeSale?.customer) || item.price;
  const [selectedRate, setSelectedRate] = useState(String(initialBaseRate));
  const [expanded, setExpanded] = useState(false);
  const [isFirstKey, setIsFirstKey] = useState(true);
  const { checkPermissionAndExecute, PermissionDialog, isPermissionDialogOpen } = usePermission();

  const toggleExpand = () => setExpanded(!expanded);

  const handlePresetClick = (text) => {
    setNote((prev) => (prev ? `${prev}, ${text}` : text));
  };

  const handleImageError = (e) => {
    e.target.src = ProductPlaceholder;
  };

  const filteredAddons = addonMappings
    .filter((mapping) => mapping.item === saleItem.productId && mapping.is_available)
    .map((mapping) => {
      const addonProduct = items.find((i) => i.id === mapping.addon_item);
      if (!addonProduct) return null;

      const finalPrice = (addonProduct.price || 0) + (mapping.price_delta || 0);

      return {
        ...addonProduct,
        price: finalPrice,
      };
    })
    .filter(Boolean);

  const handleAddAddonItem = (addonItem, price, qty) => {
    handleAddItemToSale(addonItem, qty, "", price);
  };

  const getDiscountAmount = () => {
    const rate = Number(selectedRate) || 0;
    const quantity = Number(qty) || 0;
    const percent = Number(selectedDiscount) || 0;
    if (percent <= 0) return 0;
    return NumberFormatHelper.roundOff((rate * quantity * percent) / 100);
  };

  useEffect(() => {
    if (!open) return;
    const loadNotes = async () => {
      try {
        const storedNotes = await AppSettings.getSetting(CUSTOM_PRODUCT_NOTES_KEY);
        if (storedNotes && Array.isArray(storedNotes)) {
          setProductNotes(storedNotes);
        } else {
          setProductNotes(PRESET_NOTES);
        }
      } catch (err) {
        console.error("Failed to load custom notes in dialog:", err);
        setProductNotes(PRESET_NOTES);
      }
    };
    loadNotes();
  }, [open]);

  useEffect(() => {
    if (!open || !saleItem) return;

    const initial = saleItem?.qty || 0;

    setQty(initial);
    setSelectedDiscount(String(saleItem?.discountPercentage ?? 0));

    const baseRate = selectedRate || getSaleItemRate(saleItem, activeSale?.customer);

    setSelectedRate(String(baseRate));
    setSelectedSalepersonId(saleItem?.salesperson_id || null);
    setNote(saleItem?.note || "");
  }, [
    open,
    saleItem,
    saleItem?.rate,
    item?.price,
    saleItem?.rateAndTax,
    getSaleItemRate,
    activeSale?.customer,
    selectedRate,
  ]);

  const addItem = useCallback(() => {
    setQty((prev) => Number(prev) + 1);
  }, []);

  const removeItem = useCallback(() => {
    setQty((prev) => (Number(prev) > 0 ? Number(prev) - 1 : 0));
  }, []);

  const handleSectionChange = useCallback(
    (s) => {
      const exec = () => {
        setActiveSection(s);
        setIsFirstKey(true);
      };

      if (s === ITEM_DETAILS_DIALOG_TABS.DISCOUNT) {
        checkPermissionAndExecute(STAFF_PERMISSION.DISCOUNT, exec, "Discount Access");
      } else if (s === ITEM_DETAILS_DIALOG_TABS.RATE) {
        checkPermissionAndExecute(STAFF_PERMISSION.PRICE, exec, "Price Access");
      } else {
        exec();
      }
    },
    [checkPermissionAndExecute],
  );

  function handleItemDetailsFunctionKeys(event, handleSectionChange) {
    if (event.key === KEYBOARD_KEYS.F1) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleSectionChange(ITEM_DETAILS_DIALOG_TABS.QUANTITY);
      return true;
    }
    if (event.key === KEYBOARD_KEYS.F2) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleSectionChange(ITEM_DETAILS_DIALOG_TABS.DISCOUNT);
      return true;
    }
    if (event.key === KEYBOARD_KEYS.F3) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleSectionChange(ITEM_DETAILS_DIALOG_TABS.RATE);
      return true;
    }
    if (event.key === KEYBOARD_KEYS.F4) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleSectionChange(ITEM_DETAILS_DIALOG_TABS.SALE_STAFF);
      return true;
    }
    return false;
  }

  function handleItemDetailsArrowKeys(event, activeSection, handleSectionChange) {
    if (event.key === KEYBOARD_KEYS.ARROW_DOWN || event.key === KEYBOARD_KEYS.ARROW_RIGHT) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const tabsList = Object.values(ITEM_DETAILS_DIALOG_TABS);
      const currentIndex = tabsList.indexOf(activeSection);
      if (currentIndex !== -1) {
        const nextIndex = (currentIndex + 1) % tabsList.length;
        handleSectionChange(tabsList[nextIndex]);
      }
      return true;
    }

    if (event.key === KEYBOARD_KEYS.ARROW_UP || event.key === KEYBOARD_KEYS.ARROW_LEFT) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const tabsList = Object.values(ITEM_DETAILS_DIALOG_TABS);
      const currentIndex = tabsList.indexOf(activeSection);
      if (currentIndex !== -1) {
        const prevIndex = (currentIndex - 1 + tabsList.length) % tabsList.length;
        handleSectionChange(tabsList[prevIndex]);
      }
      return true;
    }
    return false;
  }

  function handleItemDetailsQtyKeys(event, addItem, removeItem) {
    if (event.key === KEYBOARD_KEYS.PLUS || event.key === KEYBOARD_KEYS.EQUAL) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      addItem();
      return true;
    }

    if (event.key === KEYBOARD_KEYS.MINUS || event.key === KEYBOARD_KEYS.UNDERSCORE) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      removeItem();
      return true;
    }
    return false;
  }

  const handleClose = useCallback(() => {
    const finalQty = Number(qty) || 0;
    const finalRate = Number(selectedRate) || 0;

    const { rate, rateAndTax } = TaxHelper.getRateAndRateAndTax(finalRate, saleItem);

    const updatedSaleItem = updateSaleItem(
      saleItem,
      finalQty,
      selectedDiscount,
      selectedRate,
      note,
      selectedSalepersonId,
      rate,
      rateAndTax,
    );

    if (updatedSaleItem) {
      saleItemUpdateHandler(updatedSaleItem);
    }

    onClose();
  }, [qty, selectedRate, selectedDiscount, note, selectedSalepersonId, onClose, saleItem, saleItemUpdateHandler]);

  useEffect(() => {
    if (!open || isPermissionDialogOpen) return;

    const handleKeyDown = (event) => {
      const target = event.target;

      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === KEYBOARD_KEYS.ENTER) {
        if (
          target &&
          (target.role === "option" ||
            target.getAttribute?.("role") === "option" ||
            (target.classList &&
              typeof target.classList.contains === "function" &&
              target.classList.contains("MuiSelect-select")))
        ) {
          return;
        }
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        handleClose();
        return;
      }

      const isInputTarget =
        target &&
        (SCANNER_RESTRICTED_INPUTS.includes(target.tagName) ||
          target.isContentEditable ||
          target.role === "option" ||
          target.role === "combobox" ||
          target.getAttribute?.("role") === "option" ||
          target.getAttribute?.("role") === "combobox" ||
          (target.classList &&
            typeof target.classList.contains === "function" &&
            target.classList.contains("MuiSelect-select")));

      if (isInputTarget) {
        return;
      }

      if (handleItemDetailsFunctionKeys(event, handleSectionChange)) return;
      if (handleItemDetailsArrowKeys(event, activeSection, handleSectionChange)) return;
      handleItemDetailsQtyKeys(event, addItem, removeItem);
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [open, isPermissionDialogOpen, handleClose, onClose, activeSection, handleSectionChange, addItem, removeItem]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
        },
      }}
    >
      <DialogContent className={styles.dialogContent}>
        <Grid container>
          {/* image section */}
          <Grid item xs={12} sm={5} md={5} className={styles.imageSection}>
            <img
              src={item?.image && item?.image !== DEFAULT_PRODUCT_IMAGE ? item?.image : ProductPlaceholder}
              alt={item?.name}
              className={styles.image}
              onError={handleImageError}
            />
          </Grid>

          {/* Right section */}
          <Grid item xs={12} sm={7} md={7} className={styles.rightSection}>
            {/* Close icon */}
            <Grid display="flex" justifyContent={"flex-end"}>
              <IconButton onClick={handleClose} size="small" aria-label="close">
                <CloseIcon />
              </IconButton>
            </Grid>

            <Grid>
              <h3 className={styles.name}>{formatName(item?.name)}</h3>
            </Grid>

            <Grid>
              <h4 className={`${styles.description} ${expanded ? styles.expanded : styles.collapsed}`}>
                {item?.description || "No description available."}
              </h4>
              {item?.description && item?.description?.length > 80 && (
                <span className={styles.readMore} onClick={toggleExpand}>
                  {expanded ? " Read less" : " Read more"}
                </span>
              )}
            </Grid>

            <Grid>
              <div className={styles.addCartWrapper}>
                <div className={styles.btnSection}>
                  <button type="button" className={styles.qtyBtn} onClick={removeItem} aria-label="decrease">
                    <RemoveIcon style={{ fontSize: 16 }} />
                  </button>

                  <div className={styles.qtyText} aria-live="polite">
                    {qty ?? 0}
                  </div>

                  <button type="button" className={styles.qtyBtn} onClick={addItem} aria-label="increase">
                    <AddIcon style={{ fontSize: 16 }} />
                  </button>
                </div>

                <div className={styles.amountSection}>
                  <div className={styles.amount}>
                    <div className={styles.rupeePriceSection}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      <h3 className={styles.price}>{NumberFormatHelper.roundOff(selectedRate)}</h3>
                    </div>
                    <h6 className={styles.stock}>
                      (In stock {item?.stock ?? 0} {item?.unit ?? ""})
                    </h6>
                  </div>
                </div>

                {selectedDiscount ? (
                  <div className={styles.discountSection}>
                    <h6 className={styles.price}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {getDiscountAmount()}
                    </h6>
                    <h6 className={styles.discount}>({selectedDiscount}% Discount applied)</h6>
                  </div>
                ) : null}
              </div>
            </Grid>

            <div className={styles.divider}></div>

            <Grid container>
              <Grid item xs={12} md={4}>
                <div className={styles.panelWrap} style={{ gridTemplateColumns: "1fr" }}>
                  <div className={styles.leftCol} role="tablist" aria-orientation="vertical">
                    {Object.values(ITEM_DETAILS_DIALOG_TABS).map((s) => (
                      <button
                        key={s}
                        className={`${styles.leftItem} ${s === activeSection ? styles.leftItemActive : ""}`}
                        onClick={() => handleSectionChange(s)}
                        role="tab"
                        aria-selected={s === activeSection}
                        type="button"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={8} style={{ height: "162px" }}>
                <div className={styles.rightCol}>
                  {activeSection === ITEM_DETAILS_DIALOG_TABS.QUANTITY && (
                    <div className={styles.section}>
                      <KeyPadHorizontal
                        value={isFirstKey ? "" : qty}
                        onChange={(val) => {
                          setQty(val);
                          setIsFirstKey(false);
                        }}
                        allowDecimal={true}
                      />
                    </div>
                  )}

                  {/* Discount */}
                  {activeSection === ITEM_DETAILS_DIALOG_TABS.DISCOUNT && (
                    <div className={styles.section}>
                      <KeyPadHorizontal
                        value={isFirstKey ? "" : selectedDiscount || 0}
                        onChange={(val) => {
                          if (Number(val) > 100) {
                            toast.error("Discount cannot be more than 100%");
                            return;
                          }
                          setSelectedDiscount(String(val));
                          setIsFirstKey(false);
                        }}
                        allowDecimal={true}
                      />
                    </div>
                  )}

                  {/* Rate */}
                  {activeSection === ITEM_DETAILS_DIALOG_TABS.RATE && (
                    <div className={styles.section}>
                      <KeyPadHorizontal
                        value={isFirstKey ? "" : selectedRate}
                        allowDecimal={true}
                        onChange={(val) => {
                          setSelectedRate(String(val));
                          setIsFirstKey(false);
                        }}
                      />
                    </div>
                  )}

                  {/* SaleStaff */}
                  {activeSection === ITEM_DETAILS_DIALOG_TABS.SALE_STAFF && (
                    <div className={styles.section}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          id="sale-staff-select-label"
                          sx={{
                            top: "-10px",
                            display: "flex",
                            alignItems: "center",
                            height: "50px",
                          }}
                        >
                          Select Sale Staff
                        </InputLabel>
                        <Select
                          labelId="sale-staff-select-label"
                          id="sale-staff-select"
                          label="Select Staff"
                          value={selectedSalepersonId}
                          onChange={(e) => setSelectedSalepersonId(e.target.value)}
                          sx={{
                            height: "50px",
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              height: "50px",
                            },
                            borderRadius: "10px",
                          }}
                        >
                          <MenuItem value="">Select Sale Staff</MenuItem>
                          {salesPersons.map((person) => (
                            <MenuItem key={person.id} value={person.id}>
                              {person.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>

            <div className={styles.divider}></div>

            <Grid>
              <h2 className={styles.noteSection}>Custom Product Note</h2>
              <TextField
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "50px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": {
                      border: "1px dashed #aaa",
                    },
                    "&:hover fieldset": {
                      border: "1px dashed #aaa",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1px dashed #aaa",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    top: "50%",
                    left: "5%",
                    transform: "translateY(-50%)",
                    width: "100%",
                    color: "#7c7c7d",
                  },
                  "& .MuiInputLabel-shrink": {
                    top: 0,
                    transform: "translateY(-50%) scale(0.75)",
                    transformOrigin: "left",
                  },
                  "& input:-webkit-autofill": {
                    WebkitTextFillColor: "#000 !important",
                  },
                }}
              />
              <Stack direction="row" flexWrap="wrap" sx={{ mb: 1, mt: "12px", gap: "6px" }}>
                {productNotes.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    onClick={() => handlePresetClick(item)}
                    disableRipple
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "rgba(43, 43, 43, 0.4)",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "rgba(43, 43, 43, 0.4)",
                        color: "#fff",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Grid>

            <Grid className={styles.tagsWrapper} sx={{ overflowX: "auto", whiteSpace: "nowrap", display: "block" }}>
              {item?.config?.tags &&
                Object.entries(item.config.tags).map(([key, value]) => (
                  <span key={key} className={styles.tag} style={{ display: "inline-block", marginRight: "5px" }}>
                    {value}
                  </span>
                ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {filteredAddons.length > 0 && (
              <>
                <div className={styles.divider}></div>
                <Grid>
                  <h4 className={styles.description} style={{ marginTop: "10px", marginBottom: "10px" }}>
                    Addons
                  </h4>
                  <Grid container spacing={2}>
                    {filteredAddons.map((addon) => (
                      <Grid item xs={12} sm={6} md={4} key={addon.id}>
                        <ItemView item={addon} viewType="sales" onAdd={() => handleAddAddonItem(addon)} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <PermissionDialog />
    </Dialog>
  );
}
